import React, { useEffect, useMemo, useState } from 'react';
import { Ship, Receipt, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { SHIPMENT_STATUS, INVOICE_STATUS, fmtDate, fmtAmount, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, StatusBreakdown, MiniEmpty } from './DashboardShared';

const SHIPMENT_COLS = [
  {
    key: 'containerRef',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v}</span>,
  },
  {
    key: 'route',
    label: 'Route',
    render: (_, row) => (
      <span className="text-slate-500 text-[12px]">{row.departure} → {row.arrival}</span>
    ),
  },
  {
    key: 'etd',
    label: 'ETD',
    render: (v) => <span className="text-slate-500 text-[12px]">{v}</span>,
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
];

const INVOICE_COLS = [
  {
    key: 'reference',
    label: 'N° Facture',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'shipToName',
    label: 'Client',
    render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
  },
  {
    key: 'dueDate',
    label: 'Échéance',
    render: (v) => <span className="text-slate-600 text-[12px]">{v}</span>,
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
  {
    key: 'amountDisplay',
    label: 'Montant',
    align: 'right',
    render: (v) => <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">{v}</span>,
  },
];

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [invoices, setInvoices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([api.get('/shipments'), api.get('/orders'), api.get('/invoices?limit=1000')])
      .then(([sRes, oRes, iRes]) => {
        setShipments(Array.isArray(sRes.data) ? sRes.data : (sRes.data?.data ?? []));
        setOrders(oRes.data);
        setInvoices(iRes.data.rows ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const enrichedShipments = useMemo(() => {
    const orderMap = {};
    orders.forEach((o) => { orderMap[o._id] = o; });
    return shipments.map((s) => {
      const order = orderMap[s.orderId] || {};
      const mapped = SHIPMENT_STATUS[s.status] || { variant: 'info', label: s.status || '—', group: 'other' };
      return {
        ...s,
        statusVariant: mapped.variant,
        statusLabel:   mapped.label,
        statusGroup:   mapped.group,
        productName:   order.productName || '—',
        departure:     order.origin?.city || order.origin?.portCode || '—',
        arrival:       order.destination?.finalDestinationCode || order.destination?.portCode || '—',
        etd:           fmtDate(order.etd),
      };
    });
  }, [shipments, orders]);

  const enrichedInvoices = useMemo(() => invoices.map((inv) => {
    const mapped = INVOICE_STATUS[inv.status] || { variant: 'info', label: inv.status || '—', group: 'other' };
    return {
      ...inv,
      statusVariant: mapped.variant,
      statusLabel:   mapped.label,
      statusGroup:   mapped.group,
      dueDate:       fmtDate(inv.dueDate),
      amountDisplay: fmtAmount(inv.amountTTC ?? inv.amountEur, inv.fretCurrencyCode || 'EUR'),
      shipToName:    inv.shipToName || inv.billToCustomerNo || '—',
    };
  }), [invoices]);

  const kpis = useMemo(() => {
    const inTransit  = enrichedShipments.filter((s) => s.statusGroup === 'transit').length;
    const incidents  = enrichedShipments.filter((s) => s.statusGroup === 'incident').length;
    const overdue    = enrichedInvoices.filter((r) => r.statusGroup === 'overdue');
    const pending    = enrichedInvoices.filter((r) => r.statusGroup === 'pending');
    const currency   = invoices[0]?.fretCurrencyCode || 'EUR';
    const pendingAmt = pending.reduce((s, r) => s + (r.amountTTC ?? r.amountEur ?? 0), 0);
    return { inTransit, incidents, overdueCount: overdue.length, pendingAmount: pendingAmt, currency };
  }, [enrichedShipments, enrichedInvoices, invoices]);

  const monthlyData    = useMemo(() => groupByMonth(shipments, 'createdAt'), [shipments]);
  const transitRows    = useMemo(() => enrichedShipments.filter((s) => s.statusGroup === 'transit').slice(0, 5), [enrichedShipments]);
  const priorityInvRows = useMemo(() =>
    enrichedInvoices
      .filter((r) => r.statusGroup === 'overdue' || r.statusGroup === 'pending')
      .sort((a, b) => (b.ageDays || 0) - (a.ageDays || 0))
      .slice(0, 5),
    [enrichedInvoices]
  );

  const shipStatusItems = [
    { label: 'En transit', count: kpis.inTransit, variant: 'transit'   },
    { label: 'Livrées', count: enrichedShipments.filter((s) => s.statusGroup === 'delivered').length, variant: 'delivered' },
    { label: 'Incidents', count: kpis.incidents,  variant: 'incident'  },
  ];

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Administration"
        sub={`Bonjour ${user?.firstName} — vue consolidée`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Expéditions actives"
          value={loading ? '—' : kpis.inTransit}
          icon={Ship}
          sub={!loading ? `${shipments.length} expéditions au total` : undefined}
          sparkline={!loading && shipments.length ? monthlyData.map((d) => d.value) : undefined}
        />
        <KpiCard
          label="Incidents en cours"
          value={loading ? '—' : kpis.incidents}
          icon={AlertTriangle}
          trend={kpis.incidents > 0 ? 'down' : undefined}
          trendLabel={kpis.incidents > 0 ? `${kpis.incidents} à traiter` : 'Aucun incident'}
        />
        <KpiCard
          label="Factures en retard"
          value={loading ? '—' : kpis.overdueCount}
          icon={Receipt}
          trend={kpis.overdueCount > 0 ? 'down' : undefined}
          trendLabel={kpis.overdueCount > 0 ? `${kpis.overdueCount} à régulariser` : 'À jour'}
        />
        <KpiCard
          label="Montant en attente"
          value={loading ? '—' : fmtAmount(kpis.pendingAmount, kpis.currency)}
          sub={!loading ? `${invoices.length} facture${invoices.length !== 1 ? 's' : ''} au total` : undefined}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Expéditions par mois" className="col-span-2">
          {!loading && <BarChart data={monthlyData} />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Statut des expéditions">
          {loading
            ? <div className="space-y-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            : <StatusBreakdown items={shipStatusItems} total={enrichedShipments.length} />
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Expéditions en cours"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {transitRows.length}
            </span>
          }
        >
          {transitRows.length > 0
            ? <Table columns={[...SHIPMENT_COLS, ...timestampColumns]} rows={transitRows} rowKey="_id" />
            : <MiniEmpty label="Aucune expédition en cours" />
          }
        </SectionCard>

        <SectionCard
          title="Factures prioritaires"
          action={
            priorityInvRows.length > 0 && (
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                {priorityInvRows.length}
              </span>
            )
          }
        >
          {priorityInvRows.length > 0
            ? <Table columns={[...INVOICE_COLS, ...timestampColumns]} rows={priorityInvRows} rowKey="_id" />
            : <MiniEmpty label="Aucune facture prioritaire" ok />
          }
        </SectionCard>
      </div>
    </div>
  );
}
